import { useLocation } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import rootStore from '~/store';

type DisclaimerEligibility = {
  isOnboarded?: boolean;
  pathname: string;
};

type DisclaimerGateInput = DisclaimerEligibility & {
  disclaimerViewed: boolean;
};

/** Routes the Disclaimer modal never renders on. */
function isExemptPath(pathname: string): boolean {
  const path = pathname.toLowerCase();
  return path.includes('headless') || path.includes('admin');
}

/**
 * Whether the Disclaimer modal applies to this user on this route at all, accepted or not.
 * Users still in onboarding never see it, so nothing may wait on their acceptance.
 */
export function isDisclaimerEligible({ isOnboarded, pathname }: DisclaimerEligibility): boolean {
  return isOnboarded === true && !isExemptPath(pathname);
}

/** Whether the Disclaimer modal is on screen holding the app. */
export function isDisclaimerBlocking({ disclaimerViewed, ...eligibility }: DisclaimerGateInput): boolean {
  return isDisclaimerEligible(eligibility) && !disclaimerViewed;
}

/** `isDisclaimerBlocking` against live state, for anything that must not open over the Disclaimer modal. */
export function useDisclaimerBlocking(): boolean {
  const { me, disclaimerViewed } = useSnapshot(rootStore);
  const { pathname } = useLocation();

  return isDisclaimerBlocking({ disclaimerViewed, isOnboarded: me?.isOnboarded, pathname });
}
